import { useMemo } from "react";
import { Article, Combined } from "../types/article";
import { useEDSData } from "./useEDSData";
import { useSalesStopData } from "./useSalesStopData";
import { joinProductArticleIds } from "../tools";
import { EdsData, SalesStopData } from "../types/article";
import { useTasksByMedia } from "./useTasksByMedia";
import { TaskLink } from "../types/taskLink";

const getTruncatedTime = (date: string): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};
interface ArticleWithEdsAndStops extends Article {
  eds?: EdsData;
  salesStop?: SalesStopData;
}

export function useCombinedArticles(
  countryId: string,
  storeId: string,
  mediaId: string,
  articles: ArticleWithEdsAndStops[] | undefined
) {
  const productArticleIdsString = useMemo(
    () => joinProductArticleIds(articles ?? []),
    [articles]
  );

  const { data: edsList } = useEDSData(countryId, productArticleIdsString);

  const { data: salesStopList } = useSalesStopData(
    storeId,
    productArticleIdsString
  );
  const { taskLinks } = useTasksByMedia(mediaId);

  return useMemo(() => {
    if (!articles) {
      return undefined;
    }

    const normalizedEdsList = (edsList ?? []).map((eds) => {
      const normalized: EdsData = { ...eds };

      if (normalized.validToDateTime) {
        normalized.validToDate = getTruncatedTime(normalized.validToDateTime);
        normalized.validToDateTime = normalized.validToDate.toJSON();
      }
      if (normalized.validFromDateTime) {
        normalized.validFromDate = getTruncatedTime(
          normalized.validFromDateTime
        );
        normalized.validFromDateTime = normalized.validFromDate.toJSON();
      }

      return normalized;
    });

    const edsByProductArticleId = new Map<string, EdsData>();
    for (const eds of normalizedEdsList) {
      edsByProductArticleId.set(eds.productArticleId, eds);
    }

    const salesStopByProductArticleId = new Map<string, SalesStopData>();
    for (const salesStop of salesStopList ?? []) {
      salesStopByProductArticleId.set(salesStop.productArticleId, salesStop);
    }

    const taskLinksByArticleId = new Map<string, TaskLink[]>();
    for (const taskLink of taskLinks ?? []) {
      const current = taskLinksByArticleId.get(taskLink.articleId) ?? [];
      taskLinksByArticleId.set(taskLink.articleId, [...current, taskLink]);
    }

    const combinedArticles: Combined<Article>[] = articles.map((article) => ({
      ...article,
      createdAtDate: article.createdAt
        ? new Date(article.createdAt)
        : article.createdAtDate,
      eds: edsByProductArticleId.get(article.productArticleId),
      salesStop: salesStopByProductArticleId.get(article.productArticleId),
      tasks: taskLinksByArticleId.get(article.id) ?? [],
    }));

    for (const article of combinedArticles) {
      if (!article.parentArticleCombinationNumber || !article.eds) {
        continue;
      }

      const matches = combinedArticles.filter(
        (candidate) =>
          candidate.productArticleId === article.parentArticleCombinationNumber
      );

      if (matches.length === 0) {
        continue;
      }

      const today = new Date();
      const closestMatch = matches.reduce((prev, curr) => {
        const prevDate = prev.eds?.validToDateTime
          ? new Date(prev.eds.validToDateTime)
          : today;
        const currDate = curr.eds?.validToDateTime
          ? new Date(curr.eds.validToDateTime)
          : today;

        return Math.abs(currDate.getTime() - today.getTime()) <
          Math.abs(prevDate.getTime() - today.getTime())
          ? curr
          : prev;
      });

      closestMatch.eds = article.eds;
    }

    for (const article of combinedArticles) {
      if (!article.parentArticleCombinationNumber || !article.salesStop) {
        continue;
      }

      for (const candidate of combinedArticles) {
        if (
          candidate.productArticleId === article.parentArticleCombinationNumber
        ) {
          candidate.salesStop = article.salesStop;
        }
      }
    }

    return combinedArticles;
  }, [articles, edsList, salesStopList, taskLinks]);
}
